import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DollarSign, Search, Download, TrendingUp, Clock, CheckCircle2, Settings, FlaskConical } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { usePaymentTransactions } from '@/hooks/usePaymentTransactions';
import { usePlatformSetting, useUpdatePlatformSetting } from '@/hooks/usePlatformSettings';
import { usePaymentAnalyticsDaily, usePaymentSuccessByOrg } from '@/hooks/usePaymentAnalytics';
import { PaymentMetricsChart } from '@/components/payments/PaymentMetricsChart';
import { PaymentSuccessRateWidget } from '@/components/payments/PaymentSuccessRateWidget';
import { RefundManagementPanel } from '@/components/payments/RefundManagementPanel';
import { PaymentRecoveryPanel } from '@/components/payments/PaymentRecoveryPanel';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

export default function AdminPaymentsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [newFeePercentage, setNewFeePercentage] = useState<string>('');
  const [newCcFeePercentage, setNewCcFeePercentage] = useState<string>('');
  const [showTestData, setShowTestData] = useState(false);
  
  const { data: transactions, isLoading, error: transactionsError } = usePaymentTransactions({
    status: statusFilter || undefined,
    includeTestData: showTestData,
  });
  
  const { data: feeSetting, isLoading: feeLoading } = usePlatformSetting('platform_fee_percentage');
  const { data: ccFeeSetting, isLoading: ccFeeLoading } = usePlatformSetting('cc_processing_fee_percentage');
  const updateSetting = useUpdatePlatformSetting();
  const { data: analyticsDaily, isLoading: isLoadingAnalytics, error: analyticsError } = usePaymentAnalyticsDaily(30);
  const { data: successByOrg, isLoading: isLoadingSuccess, error: successError } = usePaymentSuccessByOrg();

  // Log any errors to console for debugging
  if (transactionsError) {
    console.error('Transactions error:', transactionsError);
  }
  if (analyticsError) {
    console.error('Analytics error:', analyticsError);
  }
  if (successError) {
    console.error('Success by org error:', successError);
  }

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const getStatusBadge = (status: string) => {
    const configs = {
      payment_pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
      ach_pending: { label: 'ACH Processing', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
      invoice_pending: { label: 'Invoice Sent', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
      payment_captured: { label: 'Captured', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
      held_in_escrow: { label: 'In Escrow', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
      transfer_initiated: { label: 'Transfer Initiated', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
      transfer_complete: { label: 'Complete', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      refunded: { label: 'Refunded', className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
      failed: { label: 'Failed', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
    };
    const config = configs[status as keyof typeof configs] || { label: status, className: '' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Calculate totals
  const totalVolume = transactions?.reduce((sum, t) => sum + t.total_amount_cents, 0) || 0;
  const totalFees = transactions?.reduce((sum, t) => sum + t.platform_fee_cents, 0) || 0;
  const inEscrow = transactions?.filter(t => t.status === 'held_in_escrow').reduce((sum, t) => sum + t.total_amount_cents, 0) || 0;
  const completed = transactions?.filter(t => t.status === 'transfer_complete').length || 0;
  const achPending = transactions?.filter(t => t.status === 'ach_pending').length || 0;
  const achPendingAmount = transactions?.filter(t => t.status === 'ach_pending').reduce((sum, t) => sum + t.total_amount_cents, 0) || 0;

  const filteredTransactions = transactions?.filter(t => 
    !searchTerm || 
    t.stripe_payment_intent_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.job_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUpdateFee = () => {
    const value = parseFloat(newFeePercentage);
    if (value >= 0 && value <= 100) {
      updateSetting.mutate({
        settingKey: 'platform_fee_percentage',
        newValue: newFeePercentage,
      });
      setNewFeePercentage('');
    }
  };

  const handleUpdateCcFee = () => {
    const value = parseFloat(newCcFeePercentage);
    if (value >= 0 && value <= 10) {
      updateSetting.mutate({
        settingKey: 'cc_processing_fee_percentage',
        newValue: newCcFeePercentage,
      });
      setNewCcFeePercentage('');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Error Display */}
      {(transactionsError || analyticsError || successError) && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Error loading payment data:</p>
              {transactionsError && <p>Transactions: {String(transactionsError)}</p>}
              {analyticsError && <p>Analytics: {String(analyticsError)}</p>}
              {successError && <p>Success Rate: {String(successError)}</p>}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Payment Management</h1>
          <p className="text-muted-foreground">Monitor and manage all platform payments and settings</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="show-test-payments" className="text-sm font-normal cursor-pointer">Show test orgs</Label>
            <Switch id="show-test-payments" checked={showTestData} onCheckedChange={setShowTestData} />
          </div>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <Tabs defaultValue="transactions" className="space-y-6">
        <TabsList>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="recovery">Recovery</TabsTrigger>
          <TabsTrigger value="refunds">Refunds</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-6">

      {/* ACH Pending Alert */}
      {achPending > 0 && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span>
                <strong>{achPending}</strong> ACH payment{achPending !== 1 ? 's' : ''} processing ({formatAmount(achPendingAmount)} total)
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setStatusFilter('ach_pending')}
              >
                View ACH Payments
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Volume</p>
                <p className="text-2xl font-bold">{formatAmount(totalVolume)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Platform Fees</p>
                <p className="text-2xl font-bold">{formatAmount(totalFees)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Escrow</p>
                <p className="text-2xl font-bold">{formatAmount(inEscrow)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Transactions</CardTitle>
          <CardDescription>View and manage all payment transactions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by transaction ID or job ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={statusFilter === '' ? 'all' : statusFilter}
              onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="payment_pending">Pending</SelectItem>
                <SelectItem value="ach_pending">ACH Processing</SelectItem>
                <SelectItem value="held_in_escrow">In Escrow</SelectItem>
                <SelectItem value="transfer_complete">Complete</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading transactions...</div>
          ) : !filteredTransactions || filteredTransactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No transactions found</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Job ID</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>ACH Settlement</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {format(new Date(transaction.created_at), 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Link 
                          to={`/jobs/${transaction.job_id}`}
                          className="text-primary hover:underline font-mono text-sm"
                        >
                          {transaction.job_id.slice(0, 8)}...
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {transaction.payment_method_type && (
                            <Badge variant="outline" className="w-fit">
                              {transaction.payment_method_type === 'us_bank_account' ? 'ACH' : 'Card'}
                            </Badge>
                          )}
                          {transaction.payment_method_last4 && (
                            <span className="text-xs text-muted-foreground">
                              ••••{transaction.payment_method_last4}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-semibold">
                            {formatAmount(transaction.total_amount_cents)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Fee: {formatAmount(transaction.platform_fee_cents)}
                          </span>
                          <span className="text-xs text-green-600 dark:text-green-400">
                            Payout: {formatAmount(transaction.vendor_payout_cents)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(transaction.status)}
                          {transaction.error_message && (
                            <span className="text-xs text-destructive">
                              {transaction.error_message}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {transaction.payment_method_type === 'us_bank_account' && (
                          <div className="flex flex-col gap-1 text-sm">
                            {transaction.ach_settlement_expected_at && (
                              <div className="text-muted-foreground">
                                <span className="font-medium">Expected:</span>{' '}
                                {format(new Date(transaction.ach_settlement_expected_at), 'MMM dd, HH:mm')}
                              </div>
                            )}
                            {transaction.ach_settled_at && (
                              <div className="text-green-600 dark:text-green-400">
                                <span className="font-medium">Settled:</span>{' '}
                                {format(new Date(transaction.ach_settled_at), 'MMM dd, HH:mm')}
                              </div>
                            )}
                            {!transaction.ach_settled_at && !transaction.ach_settlement_expected_at && (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        )}
                        {transaction.payment_method_type !== 'us_bank_account' && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">View Details</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Payment Analytics</h2>
              <p className="text-muted-foreground">Track payment trends and success rates</p>
            </div>
          </div>

          {isLoadingAnalytics || isLoadingSuccess ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading analytics data...
              </CardContent>
            </Card>
          ) : (!analyticsDaily || analyticsDaily.length === 0) && (!successByOrg || successByOrg.length === 0) ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No payment data available yet.</p>
                <p className="text-sm text-muted-foreground mt-2">Analytics will appear once payments are processed.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {analyticsDaily && analyticsDaily.length > 0 && (
                <>
                  <PaymentSuccessRateWidget
                    successfulPayments={analyticsDaily.reduce((sum, day) => sum + day.successful_payments, 0)}
                    failedPayments={analyticsDaily.reduce((sum, day) => sum + day.failed_payments, 0)}
                    refundedPayments={analyticsDaily.reduce((sum, day) => sum + day.refunded_payments, 0)}
                  />
                  <PaymentMetricsChart data={analyticsDaily} />
                </>
              )}

              {successByOrg && successByOrg.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Success Rate by Organization</CardTitle>
                    <CardDescription>Top organizations by transaction volume</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {successByOrg.map((org) => (
                        <div key={org.organization_id} className="flex items-center justify-between p-3 rounded-lg border">
                          <div className="flex-1">
                            <div className="font-medium">{org.organization_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {org.total_transactions} transactions
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm text-muted-foreground">Success Rate</div>
                              <div className="text-lg font-bold">{Number(org.success_rate).toFixed(1)}%</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-green-600">{org.successful_count} successful</div>
                              <div className="text-sm text-destructive">{org.failed_count} failed</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="recovery" className="space-y-6">
          <PaymentRecoveryPanel />
        </TabsContent>

        <TabsContent value="refunds" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Refund Management</h2>
              <p className="text-muted-foreground">Review and process refund requests</p>
            </div>
          </div>

          <RefundManagementPanel />
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Platform Payment Settings
              </CardTitle>
              <CardDescription>Configure platform-wide payment parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Platform Fee Configuration */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="platform-fee">Platform Fee Percentage</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Percentage charged on all transactions processed through the platform (deducted from vendor payout)
                  </p>
                </div>

                <div className="flex items-end gap-4">
                  <div className="flex-1 max-w-xs">
                    <Input
                      id="platform-fee"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={newFeePercentage || feeSetting?.setting_value || ''}
                      onChange={(e) => setNewFeePercentage(e.target.value)}
                      placeholder="Enter fee percentage (0-100)"
                      disabled={feeLoading}
                    />
                  </div>
                  <Button
                    onClick={handleUpdateFee}
                    disabled={!newFeePercentage || updateSetting.isPending || feeLoading}
                  >
                    {updateSetting.isPending ? 'Saving...' : 'Update Fee'}
                  </Button>
                </div>

                {/* Real-time preview */}
                {newFeePercentage && parseFloat(newFeePercentage) >= 0 && parseFloat(newFeePercentage) <= 100 && (
                  <Alert>
                    <AlertDescription>
                      <strong>Preview:</strong> On a $1,000 transaction:
                      <ul className="mt-2 space-y-1 text-sm">
                        <li>• Platform fee: ${((parseFloat(newFeePercentage) / 100) * 1000).toFixed(2)}</li>
                        <li>• Vendor payout: ${(1000 - (parseFloat(newFeePercentage) / 100) * 1000).toFixed(2)}</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {feeSetting && !feeLoading && (
                  <div className="text-sm text-muted-foreground space-y-1 pt-2 border-t">
                    <p><strong>Current fee:</strong> {feeSetting.setting_value}%</p>
                    <p><strong>Last updated:</strong> {new Date(feeSetting.updated_at).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* CC Processing Fee Configuration */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cc-fee">Credit Card Processing Fee Percentage</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Fee charged to clients for credit card payments (ACH payments are fee-free). This fee is added to the transaction total and is NOT refundable.
                  </p>
                </div>

                <div className="flex items-end gap-4">
                  <div className="flex-1 max-w-xs">
                    <Input
                      id="cc-fee"
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={newCcFeePercentage || ccFeeSetting?.setting_value || ''}
                      onChange={(e) => setNewCcFeePercentage(e.target.value)}
                      placeholder="Enter CC fee percentage (0-10)"
                      disabled={ccFeeLoading}
                    />
                  </div>
                  <Button
                    onClick={handleUpdateCcFee}
                    disabled={!newCcFeePercentage || updateSetting.isPending || ccFeeLoading}
                  >
                    {updateSetting.isPending ? 'Saving...' : 'Update CC Fee'}
                  </Button>
                </div>

                {/* Real-time preview */}
                {newCcFeePercentage && parseFloat(newCcFeePercentage) >= 0 && parseFloat(newCcFeePercentage) <= 10 && (
                  <Alert>
                    <AlertDescription>
                      <strong>Preview:</strong> On a $1,000 credit card transaction:
                      <ul className="mt-2 space-y-1 text-sm">
                        <li>• PO Amount: $1,000.00</li>
                        <li>• CC Processing Fee: ${((parseFloat(newCcFeePercentage) / 100) * 1000).toFixed(2)}</li>
                        <li>• <strong>Total charged to client:</strong> ${(1000 + (parseFloat(newCcFeePercentage) / 100) * 1000).toFixed(2)}</li>
                      </ul>
                      <p className="text-xs mt-2 text-muted-foreground">
                        Note: ACH bank transfers have no processing fee.
                      </p>
                    </AlertDescription>
                  </Alert>
                )}

                {ccFeeSetting && !ccFeeLoading && (
                  <div className="text-sm text-muted-foreground space-y-1 pt-2 border-t">
                    <p><strong>Current CC fee:</strong> {ccFeeSetting.setting_value}%</p>
                    <p><strong>Last updated:</strong> {new Date(ccFeeSetting.updated_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
